import { HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken, Inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  ApplicationDto,
  ApplicationDefinitionType,
  ApplicationDefinitionVo,
  Addon,
  MarketPlaceApplicationOrganization,
  UserDto,
  Group,
  ServicePrincipal,
  ServicePrincipalCredentials,
  OrganizationRole,
  OrganizationRoleUser,
  OrganizationRoleGroup,
  OrganizationRoleServicePrincipal,
  OrganizationRoleApplication,
  Environment,
  EnvironmentComponent,
  ActiveEnvironmentComponent,
  EnvironmentComponentRole,
  EnvironmentComponentOperationRoleAssignment,
  MarketPlaceApplication,
  MarketPlaceApplicationVersionDto,
  EnvironmentComponentSettingValue,
  MarketPlaceAppType,
  ModifyEnvironmentComponentDefinitionDto,
  EnvironmentComponentDefinition,
  RoleType,
  EnvironmentComponentValidation,
  EnvironmentComponentTask,
  EnvironmentTaskScheduler,
  TaskHistoryPage,
  TaskHistoryDetail,
  ApplicationVo,
  FootprintWorkflowsBlobsDataDto,
  CloneApplicationDefinitionDto,
  TaskList,
  UnifiedTaskList,
  ApplicationCanBeDeletedDto,
  EnvironmentComponentSystemSettings,
  MarketPlaceApplicationVersionBuildDto,
  ReferenceableModuleVo,
  ApplicationReferencedFromVo,
  NewUserDto,
  InviteUserResult,
  LocalUserRequest,
  CreateLocalUserResult,
  LocalAccountsAvailability,
  ResetLocalUserPasswordResult,
  InvitationStatus,
  NewGroupDto,
  ApplicationDefinitionDto,
  NewApplicationDefinitionDto,
  OrganizationVo,
  OrganizationDto,
  NewOrganizationDto,
  ApplicationStatusEnum,
  ApplicationGroupVo,
  ApplicationGroupDto,
  NewApplicationGroupDto,
  ApplicationGroupTypeEnum,
  MarketPlaceApplicationVersionVo,
  MarketPlaceApplicationVersionWithSettingsVo,
  MyAccessVo,
  MyFeatureBranchVo,
  ReferenceKeyDto,
  LicenseVo,
  LicenseSeatVo,
  LicenseDto,
  NewLicenseDto,
  AllocateLicenseSeatDto,
  ReferenceOperationDto,
  TaskHistoryFilter,
  OrganizationScheduleSummary
} from './application.do';

import { isNil } from 'lodash-es';
import { ApplicationContextService } from './application-context.service';
import { Configuration, EDesignerConfigType, IDesignerValidationError } from '../designer-config-service/designer-config.service';
import { IShellDesignerConfig } from '../designer-config-service/shell/shell-designer-config';
import { ILocalizationDesignerConfig } from '../designer-config-service/localization/localization-designer-config';
import {
  OrganizationRoleUserLink,
  OrganizationRoleGroupLink,
  OrganizationRoleServicePrincipalLink
} from '../../management/newversion/state/roles/roles.model';

export const DATEX_APP_API_URL = new InjectionToken<string>('DATEX_APP_API_URL');

@Injectable({
  providedIn: 'root'
})
export class OrganizationsService {
  protected endpointUrl: string;

  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}organizations`;
  }

  getAll(): Observable<OrganizationVo[]> {
    const url = `${this.endpointUrl}`;
    return this.http.get<OrganizationVo[]>(url);
  }

  get(id: number): Observable<OrganizationDto> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.get<OrganizationDto>(url);
  }

  getByTenant(tenantId: string): Observable<OrganizationDto> {
    const url = `${this.endpointUrl}/tenant/${tenantId}`;
    return this.http.get<OrganizationDto>(url);
  }

  create(org: NewOrganizationDto): Observable<OrganizationDto> {
    const url = `${this.endpointUrl}`;
    return this.http.post<OrganizationDto>(url, org);
  }

  update(org: OrganizationDto): Observable<OrganizationDto> {
    const url = `${this.endpointUrl}/${org.id}`;
    return this.http.put<OrganizationDto>(url, org);
  }

  delete(id: number): Observable<void> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.delete<void>(url);
  }

  isReferenceNameUnique(referenceName: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.endpointUrl}/isreferencenameunique?referenceName=${encodeURIComponent(referenceName)}`, null);
  }

  // code for newversion
  getByTenants(tenantIds: string[]): Observable<OrganizationDto[]> {
    let url = `${this.endpointUrl}/tenants?`;
    if (tenantIds) {
      url += tenantIds.map(t => `tenantIds=${t}`).join('&');
    }
    return this.http.get<OrganizationDto[]>(url);
  }

  getMine(): Observable<OrganizationDto> {
    let url = `${this.endpointUrl}/mine`;
    return this.http.get<OrganizationDto>(url);
  }

  createOrganizationWithUser(org: NewOrganizationDto): Observable<OrganizationDto> {
    const url = `${this.endpointUrl}/createwithuser`;
    return this.http.post<OrganizationDto>(url, org);
  }
}

@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {
  protected endpointUrl: string;

  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}authorization`;
  }

  hasStudioAccess(): Observable<boolean> {
    const url = `${this.endpointUrl}/hasStudioAccess`;
    return this.http.get<boolean>(url);
  }

  hasManagementAccess(): Observable<boolean> {
    const url = `${this.endpointUrl}/hasManagementAccess`;
    return this.http.get<boolean>(url);
  }

  hasDeployAccess(): Observable<boolean> {
    const url = `${this.endpointUrl}/hasDeployAccess`;
    return this.http.get<boolean>(url);
  }

  hasDatexPackagePublishAccess(): Observable<boolean> {
    const url = `${this.endpointUrl}/hasDatexPackagePublishAccess`;
    return this.http.get<boolean>(url);
  }
}

@Injectable({
  providedIn: 'root'
})
export class EnvironmentsService {
  protected endpointUrl: string;

  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}environments`;
  }

  getAll(organizationId: number): Observable<Environment[]> {
    const url = `${this.endpointUrl}?organizationId=${organizationId}`;
    return this.http.get<Environment[]>(url);
  }

  get(id: number): Observable<Environment> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.get<Environment>(url);
  }

  getSchedulerForEnvironment(id: number): Observable<EnvironmentTaskScheduler> {
    const url = `${this.endpointUrl}/${id}/schedulerinfo`;
    return this.http.get<EnvironmentTaskScheduler>(url);
  }

  create(env: Environment): Observable<Environment> {
    const url = `${this.endpointUrl}`;
    return this.http.post<Environment>(url, env);
  }

  update(env: Environment): Observable<Environment> {
    const url = `${this.endpointUrl}/${env.id}`;
    return this.http.put<Environment>(url, env);
  }

  delete(id: number): Observable<void> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.delete<void>(url);
  }

  isNameUnique(name: string, organizationId: number): Observable<boolean> {
    const url = `${this.endpointUrl}/isnameunique?name=${name}&organizationId=${organizationId}`;
    return this.http.post<boolean>(url, null);
  }

  isReferenceNameUnique(referenceName: string, organizationId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.endpointUrl}/isreferencenameunique?referenceName=${encodeURIComponent(referenceName)}&organizationId=${organizationId}`, null);
  }
}

@Injectable({
  providedIn: 'root'
})
export class MarketPlaceApplicationsService {
  protected endpointUrl: string;

  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}marketplaceapplications`;
  }

  get(id: number): Observable<MarketPlaceApplication> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.get<MarketPlaceApplication>(url);
  }

  getInstallableApplicationsForOrganization(organizationId: number): Observable<MarketPlaceApplication[]> {
    const url = `${this.endpointUrl}/installable?organizationId=${organizationId}`;
    return this.http.get<MarketPlaceApplication[]>(url);
  }

  getInstallableFootprintWorkflows(organizationId: number): Observable<MarketPlaceApplication[]> {
    const url = `${this.endpointUrl}/installablefootprintworkflows?organizationId=${organizationId}`;
    return this.http.get<MarketPlaceApplication[]>(url);
  }

  getPublic(): Observable<MarketPlaceApplication[]> {
    const url = `${this.endpointUrl}/public`;
    return this.http.get<MarketPlaceApplication[]>(url);
  }

  getFootprintWorkflowsVersions(id: number): Observable<MarketPlaceApplicationVersionDto[]> {
    const url = `${this.endpointUrl}/${id}/footprintworkflowsversions`;
    return this.http.get<MarketPlaceApplicationVersionDto[]>(url);
  }

  getAddons(marketPlaceApplicationTypeId: number, organizationId?: number): Observable<Addon[]> {
    const url = `${this.endpointUrl}/addons?marketPlaceApplicationTypeId=${marketPlaceApplicationTypeId}&organizationId=${organizationId}`;
    return this.http.get<Addon[]>(url);
  }

  getReferenceableModules(applicationId: number, referencedUniqueIdentifiers: string[]): Observable<ReferenceableModuleVo[]> {
    const params = referencedUniqueIdentifiers.map(id => `referencedUniqueIdentifiers=${encodeURIComponent(id)}`).join('&');
    const url = `${this.endpointUrl}/referenceablemodules?applicationId=${applicationId}${params.length ? '&' + params : ''}`;
    return this.http.get<ReferenceableModuleVo[]>(url);
  }
}

@Injectable({
  providedIn: 'root'
})
export class MarketPlaceApplicationVersionsService {
  protected endpointUrl: string;

  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}marketplaceapplications`;
  }

  getApplicationVersions(id: number): Observable<MarketPlaceApplicationVersionVo[]> {
    const url = `${this.endpointUrl}/${id}/versions`;
    return this.http.get<MarketPlaceApplicationVersionVo[]>(url);
  }

  getFootprintWorkflowsVersions(id: number): Observable<MarketPlaceApplicationVersionVo[]> {
    const url = `${this.endpointUrl}/${id}/versions/footprintworkflows`;
    return this.http.get<MarketPlaceApplicationVersionVo[]>(url);
  }

  // temporairy for the scheduler for now
  createApplicationVersion(id: number, version: { releaseNotes: string, versionCode: string, versionName: string, containerImageUrl: string }) {
    const url = `${this.endpointUrl}/${id}/versions`;
    return this.http.post<MarketPlaceApplicationVersionDto>(url, version);
  }

  deleteApplicationVersion(id: number, versionId: number) {
    const url = `${this.endpointUrl}/${id}/versions/${versionId}`;
    return this.http.delete(url);
  }

  refreshApplicationVersionBuild(id: number, versionId: number) {
    const url = `${this.endpointUrl}/${id}/versions/${versionId}/refreshBuildInfo`;
    return this.http.post<MarketPlaceApplicationVersionBuildDto>(url, null);
  }

  getFootprintWorkflowsDataExists(id: number, versionId: number): Observable<boolean> {
    const url = `${this.endpointUrl}/${id}/versions/${versionId}/footprintworkflowsdataexists`;
    return this.http.get<boolean>(url);
  }

  getFootprintWorkflowsData(id: number, versionId: number): Observable<FootprintWorkflowsBlobsDataDto> {
    const url = `${this.endpointUrl}/${id}/versions/${versionId}/footprintworkflowsdata`;
    return this.http.get<FootprintWorkflowsBlobsDataDto>(url);
  }
}

@Injectable({
  providedIn: 'root'
})
export class MarketPlaceApplicationVersionsWithSettingsService {
  protected endpointUrl: string;

  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}marketplaceapplicationversions`;
  }

  getByReferenceKeys(keys: ReferenceKeyDto[]): Observable<MarketPlaceApplicationVersionWithSettingsVo[]> {
    const url = `${this.endpointUrl}/byReferenceKeys`;
    return this.http.post<MarketPlaceApplicationVersionWithSettingsVo[]>(url, keys);
  }

  getByIdWithSettings(id: number): Observable<MarketPlaceApplicationVersionWithSettingsVo> {
    const url = `${this.endpointUrl}/${id}/settings`;
    return this.http.get<MarketPlaceApplicationVersionWithSettingsVo>(url);
  }

  getLatestByApplicationVersionIds(ids: number[]): Observable<MarketPlaceApplicationVersionWithSettingsVo[]> {
    const url = `${this.endpointUrl}/latestByApplicationVersionIds`;
    return this.http.post<MarketPlaceApplicationVersionWithSettingsVo[]>(url, ids);
  }
}


@Injectable({
  providedIn: 'root'
})
export class EnvironmentComponentDefinitionsService {
  protected endpointUrl: string;

  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}environmentcomponentdefinitions`;
  }

  getAll(environmentId: number): Observable<EnvironmentComponentDefinition[]> {
    const url = `${this.endpointUrl}?environmentId=${environmentId}`;
    return this.http.get<EnvironmentComponentDefinition[]>(url);
  }

  getAllComponents(environmentComponentDefinitionId: number): Observable<EnvironmentComponent[]> {
    const url = `${this.endpointUrl}/${environmentComponentDefinitionId}/components`;
    return this.http.get<EnvironmentComponent[]>(url);
  }

  get(id: number): Observable<EnvironmentComponentDefinition> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.get<EnvironmentComponentDefinition>(url);
  }

  getSystemSettings(id: number) {
    const url = `${this.endpointUrl}/${id}/systemsettings`;
    return this.http.get<EnvironmentComponentSystemSettings>(url);
  }

  create(env: ModifyEnvironmentComponentDefinitionDto): Observable<EnvironmentComponentDefinition> {
    const url = `${this.endpointUrl}`;
    return this.http.post<EnvironmentComponentDefinition>(url, env);
  }

  addVersion(id: number, marketPlaceApplicationVersionId: number): Observable<EnvironmentComponent> {
    const url = `${this.endpointUrl}/${id}/addVersion/${marketPlaceApplicationVersionId}`;
    return this.http.post<EnvironmentComponent>(url, null);
  }

  update(env: ModifyEnvironmentComponentDefinitionDto): Observable<void> {
    const url = `${this.endpointUrl}/${env.id}`;
    return this.http.put<void>(url, env);
  }

  updateSystemSettings(id: number, systemSettings: EnvironmentComponentSystemSettings) {
    const url = `${this.endpointUrl}/${id}/systemsettings`;
    return this.http.put<void>(url, systemSettings);
  }

  deploy(id: number, componentId: number): Observable<void> {
    const url = `${this.endpointUrl}/${id}/deploycomponent/${componentId}`;
    return this.http.put<void>(url, null);
  }

  delete(id: number): Observable<void> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.delete<void>(url);
  }

  isUrlUnique(url: string, environmentId: number, componentDefinitionId?: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.endpointUrl}/isurlunique?url=${url}&environmentId=${environmentId}` + (componentDefinitionId ? `&componentDefinitionId=${componentDefinitionId}` : ''), null);
  }

  isReferenceNameUnique(referenceName: string, environmentId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.endpointUrl}/isreferencenameunique?referenceName=${encodeURIComponent(referenceName)}&environmentId=${environmentId}`, null);
  }
}

@Injectable({
  providedIn: 'root'
})
export class EnvironmentComponentsService {
  protected endpointUrl: string;

  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}environmentcomponents`;
  }

  get(id: number): Observable<EnvironmentComponent> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.get<EnvironmentComponent>(url);
  }

  getActives(): Observable<ActiveEnvironmentComponent[]> {
    const url = `${this.endpointUrl}/active`;
    return this.http.get<ActiveEnvironmentComponent[]>(url);
  }

  getAuthorizedActives(): Observable<ActiveEnvironmentComponent[]> {
    const url = `${this.endpointUrl}/active/authorized`;
    return this.http.get<ActiveEnvironmentComponent[]>(url);
  }

  isValid(id: number): Observable<EnvironmentComponentValidation> {
    const url = `${this.endpointUrl}/${id}/isValid`;
    return this.http.get<EnvironmentComponentValidation>(url);
  }
  // create(env: EnvironmentComponent): Observable<EnvironmentComponent> {
  //   const url = `${this.endpointUrl}`;
  //   return this.http.post<EnvironmentComponent>(url, env);
  // }

  // update(env: EnvironmentComponent): Observable<EnvironmentComponent> {
  //   const url = `${this.endpointUrl}/${env.id}`;
  //   return this.http.put<EnvironmentComponent>(url, env);
  // }

  delete(id: number): Observable<void> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.delete<void>(url);
  }

  getSettings(id: number): Observable<EnvironmentComponentSettingValue[]> {
    const url = `${this.endpointUrl}/${id}/settings`;
    return this.http.get<EnvironmentComponentSettingValue[]>(url);
  }

  updateSettings(id: number, instance: EnvironmentComponentSettingValue[]): Observable<void> {
    const url = `${this.endpointUrl}/${id}/settings`;
    return this.http.put<void>(url, instance);
  }
}

@Injectable({
  providedIn: 'root'
})
export class EnvironmentComponentRoleService {

  protected get endpointUrl(): string {
    return `${this.apiUrl}environmentcomponents`;
  }

  constructor(
    private http: HttpClient,
    @Inject(DATEX_APP_API_URL) private apiUrl: string) {
  }

  getEnvironmentComponentRole(componentId: number, roleId): Observable<EnvironmentComponentRole> {
    return this.http.get<EnvironmentComponentRole>(`${this.endpointUrl}/${componentId}/roles/${roleId}`);
  }

  getEnvironmentComponentRoles(componentId: number): Observable<EnvironmentComponentRole[]> {
    return this.http.get<EnvironmentComponentRole[]>(`${this.endpointUrl}/${componentId}/roles`);
  }

  addRoles(componentId: number, roles: OrganizationRole[]): Observable<EnvironmentComponentRole[]> {
    const url = `${this.endpointUrl}/${componentId}/roles`;
    return this.http.post<EnvironmentComponentRole[]>(url, roles.map(x => x.id));
  }

  removeRole(componentId: number, roleId: number): Observable<void> {
    const url = `${this.endpointUrl}/${componentId}/role/${roleId}`;
    return this.http.delete<void>(url);
  }

  getEnvironmentComponentRoleAssignments(componentId: number): Observable<EnvironmentComponentOperationRoleAssignment[]> {
    return this.http.get<EnvironmentComponentOperationRoleAssignment[]>(`${this.endpointUrl}/${componentId}/roleassignments`);
  }

  getAssignedOperationsForRole(componentId: number, roleId: number): Observable<EnvironmentComponentOperationRoleAssignment[]> {
    return this.http.get<EnvironmentComponentOperationRoleAssignment[]>(`${this.endpointUrl}/${componentId}/roles/${roleId}/operations`);
  }

  /** Full operation catalog for the component (host's own + every transitive reference's
   *  operations), without subtracting any role's assignments. */
  getComponentOperations(componentId: number): Observable<ReferenceOperationDto[]> {
    return this.http.get<ReferenceOperationDto[]>(`${this.endpointUrl}/${componentId}/operations`);
  }

  /** Add operation→role assignments. Each operation is keyed by (name, applicationReferenceName).
   *  Endpoint returns 204; the only caller re-fetches via getAssignedOperationsForRole. */
  addOperationsAssignmentsForRole(componentId: number, roleId: number, operations: { name: string; applicationReferenceName: string }[]): Observable<void> {
    const url = `${this.endpointUrl}/${componentId}/roles/${roleId}/operations`;
    return this.http.post<void>(url, operations);
  }

  /** Remove operation→role assignments. Each operation is keyed by (name, applicationReferenceName).
   *  All-or-none: backend throws if any key isn't currently assigned. Endpoint returns 204. */
  removeOperationRoleAssignmentsForRole(componentId: number, roleId: number, operations: { name: string; applicationReferenceName: string }[]): Observable<void> {
    const url = `${this.endpointUrl}/${componentId}/roles/${roleId}/operations`;
    return this.http.request<void>('DELETE', url, { body: operations });
  }
}

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  protected endpointUrl: string;

  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}users`;
  }

  getAll(organizationId: number): Observable<UserDto[]> {
    const url = `${this.endpointUrl}?organizationId=${organizationId}`;
    return this.http.get<UserDto[]>(url);
  }

  get(id: number): Observable<UserDto> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.get<UserDto>(url);
  }

  /** Returns the roles and data access groups the current user is assigned
   *  to in their current organization. Drives the Manager home page's
   *  "Your access" card. No org id: selecting an organization switches the
   *  MSAL active account to that organization's tenant, so the backend
   *  resolves it from the token — the same way getMyFeatures() works. */
  getMyAccess(): Observable<MyAccessVo> {
    return this.http.get<MyAccessVo>(`${this.endpointUrl}/me/access`);
  }

  create(users: NewUserDto[]): Observable<UserDto[]> {
    const url = `${this.endpointUrl}`;
    return this.http.post<UserDto[]>(url, users);
  }

  invite(emails: string[], hasManagementAccess: boolean, hasStudioAccess: boolean, hasDeployAccess: boolean, organizationRoleIds: number[], redirectEnvironmentComponentId?: number): Observable<InviteUserResult[]> {
    const url = `${this.endpointUrl}/invite`;
    return this.http.post<InviteUserResult[]>(url, { emails, hasManagementAccess, hasStudioAccess, hasDeployAccess, organizationRoleIds, redirectEnvironmentComponentId });
  }

  createLocalUsers(users: LocalUserRequest[], hasManagementAccess: boolean, hasStudioAccess: boolean, hasDeployAccess: boolean, organizationRoleIds: number[], forceChangePasswordNextSignIn: boolean): Observable<CreateLocalUserResult[]> {
    const url = `${this.endpointUrl}/local`;
    return this.http.post<CreateLocalUserResult[]>(url, { users, hasManagementAccess, hasStudioAccess, hasDeployAccess, organizationRoleIds, forceChangePasswordNextSignIn });
  }

  getLocalAccountsAvailability(): Observable<LocalAccountsAvailability> {
    return this.http.get<LocalAccountsAvailability>(`${this.endpointUrl}/local-accounts-availability`);
  }

  resetLocalUserPassword(userId: number, forceChangePasswordNextSignIn: boolean): Observable<ResetLocalUserPasswordResult> {
    const url = `${this.endpointUrl}/${userId}/local/reset-password`;
    return this.http.post<ResetLocalUserPasswordResult>(url, { forceChangePasswordNextSignIn });
  }

  setLocalUserEnabled(userId: number, enabled: boolean): Observable<void> {
    const url = `${this.endpointUrl}/${userId}/local/enabled`;
    return this.http.put<void>(url, { enabled });
  }

  resendInvite(userIds: number[]): Observable<InviteUserResult[]> {
    const url = `${this.endpointUrl}/resend-invite`;
    return this.http.post<InviteUserResult[]>(url, { userIds });
  }

  syncDisplayNames(): Observable<void> {
    const url = `${this.endpointUrl}/sync-display-names`;
    return this.http.post<void>(url, {});
  }

  getInvitationStatuses(externalIds: string[]): Observable<InvitationStatus[]> {
    const url = `${this.endpointUrl}/invitation-statuses`;
    return this.http.post<InvitationStatus[]>(url, externalIds);
  }

  update(user: UserDto): Observable<UserDto> {
    const url = `${this.endpointUrl}/${user.id}`;
    return this.http.put<UserDto>(url, user);
  }

  updateManagementAccess(userId: number, hasManagementAccess: boolean): Observable<void> {
    const url = `${this.endpointUrl}/${userId}/managementaccess`;
    return this.http.put<void>(url, { hasManagementAccess });
  }

  updateDeployAccess(userId: number, hasDeployAccess: boolean): Observable<void> {
    const url = `${this.endpointUrl}/${userId}/deployaccess`;
    return this.http.put<void>(url, { hasDeployAccess });
  }

  updateStudioAccess(userId: number, hasStudioAccess: boolean): Observable<void> {
    const url = `${this.endpointUrl}/${userId}/studioaccess`;
    return this.http.put<void>(url, { hasStudioAccess });
  }

  updateUserRoles(userId: number, addedRoleIds: number[], removedRoleIds: number[], addedDAGIds: number[], removedDAGIds: number[]): Observable<void> {
    const url = `${this.endpointUrl}/${userId}/roles`;
    return this.http.put<void>(url, { addedRoleIds, removedRoleIds, addedDAGIds, removedDAGIds });
  }

  delete(id: number): Observable<void> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.delete<void>(url);
  }

  deleteUsers(userIds: number[]): Observable<void> {
    const url = `${this.endpointUrl}`;
    return this.http.delete<void>(url, { body: { userIds } });
  }

  manageUsers(usersToCreate: NewUserDto[], userIdsToDelete: number[]): Observable<void> {
    const url = `${this.endpointUrl}/manage`;
    return this.http.post<void>(url, {
      usersToCreate: usersToCreate,
      userIdsToDelete: userIdsToDelete
    });
  }
}



@Injectable({
  providedIn: 'root'
})
export class GroupsService {
  protected endpointUrl: string;

  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}groups`;
  }

  getAll(organizationId: number): Observable<Group[]> {
    const url = `${this.endpointUrl}?organizationId=${organizationId}`;
    return this.http.get<Group[]>(url);
  }

  get(id: number): Observable<Group> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.get<Group>(url);
  }

  create(groups: NewGroupDto[]): Observable<Group[]> {
    const url = `${this.endpointUrl}`;
    return this.http.post<Group[]>(url, groups);
  }

  update(group: Group): Observable<Group> {
    const url = `${this.endpointUrl}/${group.id}`;
    return this.http.put<Group>(url, group);
  }

  updateRoles(
    groupId: number,
    addedRoleIds: number[],
    removedRoleIds: number[],
    addedDAGIds: number[],
    removedDAGIds: number[]): Observable<void> {
    const url = `${this.endpointUrl}/${groupId}/roles`;
    return this.http.put<void>(url, { addedRoleIds, removedRoleIds, addedDAGIds, removedDAGIds });
  }

  delete(id: number): Observable<void> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.delete<void>(url);
  }

  deleteGroups(ids: number[]): Observable<void> {
    const url = `${this.endpointUrl}`;
    return this.http.delete<void>(url, { body: ids });
  }

  manageGroups(groupsToCreate: NewGroupDto[], groupIdsToDelete: number[]): Observable<void> {
    const url = `${this.endpointUrl}/manage`;
    return this.http.post<void>(url, {
      groupsToCreate: groupsToCreate,
      groupIdsToDelete: groupIdsToDelete
    });
  }
}

@Injectable({
  providedIn: 'root'
})
export class ServicePrincipalsService {
  protected endpointUrl: string;

  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}servicePrincipals`;
  }

  /** Service principals in the current organization. No org id: selecting an
   *  organization switches the MSAL active account to that organization's
   *  tenant, so the backend scopes from the token. */
  getAll(): Observable<ServicePrincipal[]> {
    const url = `${this.endpointUrl}`;
    return this.http.get<ServicePrincipal[]>(url);
  }

  getCredentials(): Observable<ServicePrincipalCredentials[]> {
    const url = `${this.endpointUrl}/credentials`;
    return this.http.get<ServicePrincipalCredentials[]>(url);
  }

  get(id: number): Observable<ServicePrincipal> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.get<ServicePrincipal>(url);
  }

  create(servicePrincipals: ServicePrincipal[]): Observable<ServicePrincipal[]> {
    const url = `${this.endpointUrl}`;
    return this.http.post<ServicePrincipal[]>(url, servicePrincipals);
  }

  update(servicePrincipal: ServicePrincipal): Observable<ServicePrincipal> {
    const url = `${this.endpointUrl}/${servicePrincipal.id}`;
    return this.http.put<ServicePrincipal>(url, servicePrincipal);
  }

  updateServicePrincipalRoles(servicePrincipalId: number, addedRoleIds: number[], removedRoleIds: number[], addedDAGIds: number[], removedDAGIds: number[]): Observable<void> {
    const url = `${this.endpointUrl}/${servicePrincipalId}/roles`;
    return this.http.put<void>(url, { addedRoleIds, removedRoleIds, addedDAGIds, removedDAGIds });
  }

  delete(id: number): Observable<void> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.delete<void>(url);
  }

  deleteServicePrincipals(servicePrincipalIds: number[]): Observable<void> {
    const url = `${this.endpointUrl}`;
    return this.http.delete<void>(url, { body: servicePrincipalIds });
  }

  manageServicePrincipals(servicePrincipalsToCreate: ServicePrincipal[], servicePrincipalIdsToDelete: number[]): Observable<void> {
    const url = `${this.endpointUrl}/manage`;
    return this.http.post<void>(url, {
      servicePrincipalsToCreate: servicePrincipalsToCreate,
      servicePrincipalIdsToDelete: servicePrincipalIdsToDelete
    });
  }
}

@Injectable({
  providedIn: 'root'
})
export class ApplicationDefinitionService {
  protected endpointUrl: string;

  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}applicationdefinitions`;
  }

  getAll(applicationDefinitionTypeIds?: number[], organizationId?: number): Observable<ApplicationDefinitionVo[]> {
    let url = `${this.endpointUrl}?`;
    if (applicationDefinitionTypeIds) {
      for (const id of applicationDefinitionTypeIds) {
        url += `&applicationDefinitionTypeIds=${id}`;
      }
    }
    if (organizationId) {
      url += `${url.endsWith('?') ? '' : '&'}organizationId=${organizationId}`;
    }
    return this.http.get<ApplicationDefinitionVo[]>(url);
  }

  get(id: number): Observable<ApplicationDefinitionDto> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.get<ApplicationDefinitionDto>(url);
  }

  create(app: NewApplicationDefinitionDto): Observable<ApplicationDefinitionDto> {
    const url = `${this.endpointUrl}`;
    return this.http.post<ApplicationDefinitionDto>(url, app);
  }

  update(app: ApplicationDefinitionDto): Observable<ApplicationDefinitionDto> {
    const url = `${this.endpointUrl}/${app.id}`;
    return this.http.put<ApplicationDefinitionDto>(url, app);
  }

  clone(id: number, app: CloneApplicationDefinitionDto): Observable<ApplicationDefinitionDto> {
    const url = `${this.endpointUrl}/${id}/clone`;
    return this.http.post<ApplicationDefinitionDto>(url, app);
  }

  delete(id: number): Observable<void> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.delete<void>(url);
  }

  deleteFootprint(id: number): Observable<void> {
    const url = `${this.endpointUrl}/${id}/footprint`;
    return this.http.delete<void>(url);
  }

  isNameUnique(applicationDefinitionTypeId: number, name: string, skipAppDefId?: number, organizationId?: number): Observable<boolean> {
    let url = `${this.endpointUrl}/isnameunique?applicationDefinitionTypeId=${applicationDefinitionTypeId}&name=${name}`;

    if (isNil(skipAppDefId) === false) {
      url += `&skipapplicationdefinitionid=${skipAppDefId}`;
    }

    if (isNil(organizationId) === false) {
      url += `&organizationId=${organizationId}`;
    }

    return this.http.post<boolean>(url, null);
  }

  isIdentifierUnique(identifier: string, skipAppDefId?: number): Observable<boolean> {
    let url = `${this.endpointUrl}/isidentifierunique?identifier=${identifier}`;
    if (isNil(skipAppDefId) === false) {
      url += `&skipapplicationdefinitionid=${skipAppDefId}`;
    }
    return this.http.post<boolean>(url, null);
  }

  isOwner(id: number): Observable<boolean> {
    const url = `${this.endpointUrl}/${id}/isowner`;
    return this.http.get<boolean>(url);
  }

  provision(id: number): Observable<void> {
    const url = `${this.endpointUrl}/${id}/provision`;
    return this.http.post<void>(url, null);
  }
}

@Injectable({
  providedIn: 'root'
})
export class ApplicationGroupService {
  protected endpointUrl: string;

  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}applicationGroups`;
  }

  getAll(applicationDefinitionId: number, applicationGroupTypeId: ApplicationGroupTypeEnum = null): Observable<ApplicationGroupVo[]> {
    let url = `${this.endpointUrl}?applicationDefinitionId=${applicationDefinitionId}`;
    if (applicationGroupTypeId) {
      url += `&applicationGroupTypeId=${applicationGroupTypeId}`;
    }
    return this.http.get<ApplicationGroupVo[]>(url);
  }

  get(id: number): Observable<ApplicationGroupDto> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.get<ApplicationGroupDto>(url);
  }

  create(applicationGroup: NewApplicationGroupDto): Observable<ApplicationGroupDto> {
    const url = `${this.endpointUrl}`;
    return this.http.post<ApplicationGroupDto>(url, applicationGroup);
  }

  update(applicationGroup: ApplicationGroupDto): Observable<ApplicationGroupDto> {
    const url = `${this.endpointUrl}/${applicationGroup.id}`;
    return this.http.put<ApplicationGroupDto>(url, applicationGroup);
  }

  delete(id: number): Observable<void> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.delete<void>(url);
  }
}

@Injectable({
  providedIn: 'root'
})
export class MarketPlaceApplicationOrganizationService {
  protected endpointUrl: string;
  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}MarketPlaceApplicationorganizations`;
  }

  create(marketPlaceApplicationOrganization: MarketPlaceApplicationOrganization): Observable<MarketPlaceApplicationOrganization> {
    const url = `${this.endpointUrl}`;
    return this.http.post<MarketPlaceApplicationOrganization>(url, marketPlaceApplicationOrganization);
  }

  delete(marketPlaceApplicationId: number, organizationId: number): Observable<void> {
    const url = `${this.endpointUrl}/MarketPlaceApplication/${marketPlaceApplicationId}/organization/${organizationId}`;
    return this.http.delete<void>(url);
  }
}

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {
  protected endpointUrl: string;

  constructor(
    private http: HttpClient,
    @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}applications`;
  }

  preview(id: number): Observable<ApplicationDto> {
    const url = `${this.endpointUrl}/${id}/preview`;
    return this.http.get<ApplicationDto>(url);
  }

  getAll(applicationGroupId: number, applicationStatusIds?: ApplicationStatusEnum[]): Observable<ApplicationVo[]> {
    let url = `${this.endpointUrl}?applicationGroupId=${applicationGroupId}`;
    if (applicationStatusIds) {
      for (const id of applicationStatusIds) {
        url += `&applicationStatusIds=${id}`;
      }
    }

    return this.http.get<ApplicationVo[]>(url);
  }

  /** Open Feature branches authored by the current user in the current
   *  organization, newest-activity-first. Drives the Manager home page's
   *  "My branches" list. */
  getMyFeatures(): Observable<MyFeatureBranchVo[]> {
    return this.http.get<MyFeatureBranchVo[]>(`${this.endpointUrl}/myFeatures`);
  }

  getReferenceableModules(id: number, skipMarketPlaceApplicationId?: number): Observable<ReferenceableModuleVo[]> {
    let url = `${this.endpointUrl}/referenceableModules?applicationId=${id}`;
    if (skipMarketPlaceApplicationId) {
      url += `&skipMarketPlaceApplicationId=${skipMarketPlaceApplicationId}`;
    }
    return this.http.get<ReferenceableModuleVo[]>(url);
  }

  get(id: number): Observable<ApplicationDto> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.get<ApplicationDto>(url);
  }

  getShell(id: number): Observable<Configuration<IShellDesignerConfig>> {
    const url = `${this.endpointUrl}/${id}/shell`;
    return this.http.get<Configuration<IShellDesignerConfig>>(url);
  }

  getLocalization(id: number): Observable<Configuration<ILocalizationDesignerConfig>> {
    const url = `${this.endpointUrl}/${id}/localization`;
    return this.http.get<Configuration<ILocalizationDesignerConfig>>(url);
  }

  getConfigId(id: number, type: EDesignerConfigType): Observable<number> {
    const url = `${this.endpointUrl}/${id}/${type}/configId`;
    return this.http.get<number>(url);
  }

  publish(id: number, publishingDescription: string, version: string): Observable<void> {
    const url = `${this.endpointUrl}/${id}/publish`;
    return this.http.post<void>(url, { version, releaseNotes: publishingDescription });
  }

  delete(id: number): Observable<void> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.delete<void>(url);
  }

  validate(id: number): Observable<IDesignerValidationError[]> {
    return this.http.post<IDesignerValidationError[]>(`${this.endpointUrl}/${id}/validate`, null);
  }

  canDelete(id: number): Observable<ApplicationCanBeDeletedDto> {
    const url = `${this.endpointUrl}/${id}/candelete`;
    return this.http.get<ApplicationCanBeDeletedDto>(url);
  }

  referencedFrom(id: number): Observable<ApplicationReferencedFromVo[]> {
    const url = `${this.endpointUrl}/${id}/referencedfrom`;
    return this.http.get<ApplicationReferencedFromVo[]>(url);
  }

  getDefaultMain(applicationDefinitionId: number): Observable<ApplicationDto> {
    const url = `${this.endpointUrl}/defaultMain?applicationdefinitionid=${applicationDefinitionId}`;
    return this.http.get<ApplicationDto>(url);
  }

  getMarketplaceApplicationVersion(id: number): Observable<MarketPlaceApplicationVersionDto> {
    const url = `${this.endpointUrl}/${id}/marketPlaceApplicationVersion`;
    return this.http.get<MarketPlaceApplicationVersionDto>(url);
  }
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationEnvironmentComponentsService {
  protected get endpointUrl(): string {
    return `${this.apiUrl}organizations`
  }

  constructor(
    private http: HttpClient,
    @Inject(DATEX_APP_API_URL) private apiUrl: string) {
  }

  getActives(organizationId: number): Observable<ActiveEnvironmentComponent[]> {
    const url = `${this.endpointUrl}/${organizationId}/environmentComponents/active`;
    return this.http.get<ActiveEnvironmentComponent[]>(url);
  }
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationTasksService {
  protected get endpointUrl(): string {
    return `${this.apiUrl}organizations`;
  }

  constructor(
    private http: HttpClient,
    @Inject(DATEX_APP_API_URL) private apiUrl: string) {
  }

  getScheduledTasks(organizationId: number): Observable<UnifiedTaskList> {
    const url = `${this.endpointUrl}/${organizationId}/schedules`;
    return this.http.get<UnifiedTaskList>(url);
  }

  getScheduleSummary(organizationId: number, topFailingCount = 10): Observable<OrganizationScheduleSummary> {
    const url = `${this.endpointUrl}/${organizationId}/schedules/summary?topFailingCount=${topFailingCount}`;
    return this.http.get<OrganizationScheduleSummary>(url);
  }
}

@Injectable({
  providedIn: 'root'
})
export class EnvironmentComponentTasksService {
  protected get endpointUrl(): string {
    return `${this.apiUrl}environmentComponents`;
  }

  constructor(
    private http: HttpClient,
    @Inject(DATEX_APP_API_URL) private apiUrl: string) {
  }

  getScheduledTasks(componentId: number): Observable<TaskList> {
    const url = `${this.endpointUrl}/${componentId}/scheduledtasks?WithExecutionDetails=true`;
    return this.http.get<TaskList>(url);
  }

  getScheduledTask(componentId: number, name: string): Observable<EnvironmentComponentTask> {
    return this.getScheduledTasks(componentId)
      .pipe(map(x => x.tasks.find(y => y.name === name)));
  }

  getTaskHistory(componentId: number, name: string, filter: TaskHistoryFilter): Observable<TaskHistoryPage> {
    let url = `${this.endpointUrl}/${componentId}/scheduledtasks/history?name=${encodeURIComponent(name)}&skip=${filter.skip}&take=${filter.take}`;
    (filter.statuses ?? []).forEach(status => url += `&status=${encodeURIComponent(status)}`);
    if (filter.minDate) {
      url += `&minDate=${encodeURIComponent(new Date(filter.minDate).toISOString())}`;
    }
    if (filter.maxDate) {
      url += `&maxDate=${encodeURIComponent(new Date(filter.maxDate).toISOString())}`;
    }
    return this.http.get<TaskHistoryPage>(url);
  }

  getTaskHistoryDetail(componentId: number, name: string, runId: string): Observable<TaskHistoryDetail> {
    return this.http.get<TaskHistoryDetail>(`${this.endpointUrl}/${componentId}/scheduledtasks/history/${runId}?name=${encodeURIComponent(name)}`);
  }

  create(componentId: number, task: EnvironmentComponentTask): Observable<void> {
    return this.http.post<void>(`${this.endpointUrl}/${componentId}/scheduledtasks`, task);
  }

  update(componentId: number, task: EnvironmentComponentTask): Observable<void> {
    return this.http.put<void>(`${this.endpointUrl}/${componentId}/scheduledtasks?name=${encodeURIComponent(task.name)}`, task);
  }

  runTask(componentId: number, name: string): Observable<void> {
    return this.http.post<void>(`${this.endpointUrl}/${componentId}/scheduledtasks/runtask?name=${encodeURIComponent(name)}`, null);
  }

  deleteTask(componentId: number, name: string): Observable<void> {
    return this.http.delete<void>(`${this.endpointUrl}/${componentId}/scheduledtasks?name=${encodeURIComponent(name)}`);
  }

  activateTask(componentId: number, name: string): Observable<void> {
    return this.http.post<void>(`${this.endpointUrl}/${componentId}/scheduledtasks/activatetask?name=${encodeURIComponent(name)}`, null);
  }

  deactivateTask(componentId: number, name: string): Observable<void> {
    return this.http.post<void>(`${this.endpointUrl}/${componentId}/scheduledtasks/deactivatetask?name=${encodeURIComponent(name)}`, null);
  }

  cancelRun(componentId: number, taskName: string, runId: string): Observable<void> {
    return this.http.post<void>(`${this.endpointUrl}/${componentId}/scheduledtasks/cancelrun?name=${encodeURIComponent(taskName)}&runId=${runId}`, null);
  }

  isNameUnique(componentId: number, name: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.endpointUrl}/${componentId}/scheduledtasks/isnameunique?name=${encodeURIComponent(name)}`);
  }
}

class BaseOrganizationRoleService {

  protected get endpointUrl(): string {
    return `${this.apiUrl}organizations/${this.applicationContextService.getCurrentOrganization()?.id}/${this.controller}`;
  }

  constructor(
    private http: HttpClient,
    private applicationContextService: ApplicationContextService,
    @Inject(DATEX_APP_API_URL) private apiUrl: string,
    private controller: string) {
  }

  getAll(organizationId: number = undefined): Observable<OrganizationRole[]> {
    const url = organizationId ? `${this.apiUrl}organizations/${organizationId}/${this.controller}` : this.endpointUrl;
    return this.http.get<OrganizationRole[]>(url);
  }

  getAssignedUsers(roleId): Observable<OrganizationRoleUser[]> {
    return this.http.get<OrganizationRoleUser[]>(`${this.endpointUrl}/${roleId}/users`);
  }

  getAssignedGroups(roleId): Observable<OrganizationRoleGroup[]> {
    return this.http.get<OrganizationRoleGroup[]>(`${this.endpointUrl}/${roleId}/groups`);
  }

  getAssignedServicePrincipals(roleId): Observable<OrganizationRoleServicePrincipal[]> {
    return this.http.get<OrganizationRoleServicePrincipal[]>(`${this.endpointUrl}/${roleId}/servicePrincipals`);
  }

  getAssignedApplications(roleId): Observable<OrganizationRoleApplication[]> {
    return this.http.get<OrganizationRoleApplication[]>(`${this.endpointUrl}/${roleId}/applications`);
  }

  assignUsers(role: number | OrganizationRole, users: number[] | UserDto[], organizationId?: number): Observable<OrganizationRoleUser[]> {
    const roleId = typeof role === "number" ? role : role.id;
    const userIds = users.length > 0
      ? typeof users[0] === "number"
        ? users
        : users.map(x => x.id)
      : [];
    const baseUrl = organizationId ? `${this.apiUrl}organizations/${organizationId}/${this.controller}` : this.endpointUrl;
    const url = `${baseUrl}/${roleId}/users`
    return this.http.post<OrganizationRoleUser[]>(url, userIds);
  }

  unassignUser(assignment: { roleId: number, userId: number }): Observable<void> {
    const url = `${this.endpointUrl}/${assignment.roleId}/users/${assignment.userId}`;
    return this.http.delete<void>(url);
  }

  assignGroups(role: OrganizationRole, groups: Group[]): Observable<OrganizationRoleGroup[]> {
    const url = `${this.endpointUrl}/${role.id}/groups`;
    return this.http.post<OrganizationRoleGroup[]>(url, groups.map(x => x.id));
  }

  unassignGroup(assignment: OrganizationRoleGroup): Observable<void> {
    const url = `${this.endpointUrl}/${assignment.roleId}/groups/${assignment.groupId}`;
    return this.http.delete<void>(url);
  }

  assignServicePrincipals(role: OrganizationRole, servicePrincipals: ServicePrincipal[]): Observable<OrganizationRoleServicePrincipal[]> {
    const url = `${this.endpointUrl}/${role.id}/servicePrincipals`;
    return this.http.post<OrganizationRoleServicePrincipal[]>(url, servicePrincipals.map(x => x.id));
  }

  unassignServicePrincipal(assignment: OrganizationRoleServicePrincipal): Observable<void> {
    const url = `${this.endpointUrl}/${assignment.roleId}/servicePrincipals/${assignment.servicePrincipalId}`;
    return this.http.delete<void>(url);
  }

  get(id: number): Observable<OrganizationRole> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.get<OrganizationRole>(url);
  }

  create(role: OrganizationRole): Observable<OrganizationRole> {
    return this.http.post<OrganizationRole>(this.endpointUrl, role);
  }

  update(role: OrganizationRole): Observable<OrganizationRole> {
    const url = `${this.endpointUrl}/${role.id}`;
    return this.http.put<OrganizationRole>(url, role);
  }

  delete(role: OrganizationRole): Observable<void> {
    const url = `${this.endpointUrl}/${role.id}`;
    return this.http.delete<void>(url);
  }

  getAllUserLinks(organizationId: number): Observable<OrganizationRoleUserLink[]> {
    const url = `${this.apiUrl}organizations/${organizationId}/${this.controller}/roleuserlinks`;
    return this.http.get<OrganizationRoleUserLink[]>(url);
  }

  getAllGroupLinks(organizationId: number): Observable<OrganizationRoleGroupLink[]> {
    const url = `${this.apiUrl}organizations/${organizationId}/${this.controller}/rolegrouplinks`;
    return this.http.get<OrganizationRoleGroupLink[]>(url);
  }

  getAllServicePrincipalLinks(organizationId: number): Observable<OrganizationRoleServicePrincipalLink[]> {
    const url = `${this.apiUrl}organizations/${organizationId}/${this.controller}/roleserviceprincipallinks`;
    return this.http.get<OrganizationRoleServicePrincipalLink[]>(url);
  }

  add(organizationId: number, role: OrganizationRole): Observable<OrganizationRole> {
    return this.http.post<OrganizationRole>(`${this.apiUrl}organizations/${organizationId}/${this.controller}`, role);
  }

  updateRolesWithUGS(
    organizationId: number,
    roles: {
      roleId: number,
      roleToUpdate: OrganizationRole,
      addedUserIds: number[],
      removedUserIds: number[],
      addedGroupIds: number[],
      removedGroupIds: number[],
      addedServicePrincipalIds: number[],
      removedServicePrincipalIds: number[]
    }[]
  ): Observable<void> {
    const url = `${this.apiUrl}organizations/${organizationId}/${this.controller}/updateWithUGS`;
    return this.http.put<void>(url, {
      organizationId,
      roles
    });
  }

  deleteMultiple(organizationId: number, ids: number[]): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}organizations/${organizationId}/${this.controller}`, { body: ids });
  }
}

export interface IOrganizationRoleService {
  getAll(): Observable<OrganizationRole[]>;
  getAssignedUsers(roleId): Observable<OrganizationRoleUser[]>;
  getAssignedGroups(roleId): Observable<OrganizationRoleGroup[]>;
  getAssignedServicePrincipals(roleId): Observable<OrganizationRoleServicePrincipal[]>;
  getAssignedApplications(roleId): Observable<OrganizationRoleApplication[]>;
  assignUsers(role: OrganizationRole, users: UserDto[]): Observable<OrganizationRoleUser[]>;
  unassignUser(assignment: OrganizationRoleUser): Observable<void>;
  assignGroups(role: OrganizationRole, groups: Group[]): Observable<OrganizationRoleGroup[]>;
  unassignGroup(assignment: OrganizationRoleGroup): Observable<void>;
  assignServicePrincipals(role: OrganizationRole, servicePrincipals: ServicePrincipal[]): Observable<OrganizationRoleServicePrincipal[]>;
  unassignServicePrincipal(assignment: OrganizationRoleServicePrincipal): Observable<void>;
  get(id: number): Observable<OrganizationRole>;
  create(role: OrganizationRole): Observable<OrganizationRole>;
  update(role: OrganizationRole): Observable<OrganizationRole>;
  delete(role: OrganizationRole): Observable<void>;
  getAllUserLinks(organizationId: number): Observable<OrganizationRoleUserLink[]>;
  getAllGroupLinks(organizationId: number): Observable<OrganizationRoleGroupLink[]>;
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationRoleService extends BaseOrganizationRoleService implements IOrganizationRoleService {
  constructor(
    http: HttpClient,
    applicationContextService: ApplicationContextService,
    @Inject(DATEX_APP_API_URL) apiUrl: string) {
    super(http, applicationContextService, apiUrl, 'roles');
  }
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationDataAccessGroupService extends BaseOrganizationRoleService implements IOrganizationRoleService {
  constructor(
    http: HttpClient,
    applicationContextService: ApplicationContextService,
    @Inject(DATEX_APP_API_URL) apiUrl: string) {
    super(http, applicationContextService, apiUrl, 'OrganizationDataAccessGroups');
  }
}

@Injectable({
  providedIn: 'root'
})
export class LicensesService {
  protected endpointUrl: string;

  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}licenses`;
  }

  getAll(organizationId?: number): Observable<LicenseVo[]> {
    // the per-organization screen scopes to the selected organization; the Datex-wide
    // licensing view omits it and sees every organization
    const url = organizationId != null ? `${this.endpointUrl}?organizationId=${organizationId}` : `${this.endpointUrl}`;
    return this.http.get<LicenseVo[]>(url);
  }

  get(id: number): Observable<LicenseDto> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.get<LicenseDto>(url);
  }

  create(license: NewLicenseDto): Observable<LicenseDto> {
    const url = `${this.endpointUrl}`;
    return this.http.post<LicenseDto>(url, license);
  }

  update(license: LicenseDto): Observable<LicenseDto> {
    const url = `${this.endpointUrl}/${license.id}`;
    return this.http.put<LicenseDto>(url, license);
  }

  delete(id: number): Observable<void> {
    const url = `${this.endpointUrl}/${id}`;
    return this.http.delete<void>(url);
  }
}

@Injectable({
  providedIn: 'root'
})
export class LicenseSeatsService {
  protected endpointUrl: string;

  constructor(private http: HttpClient, @Inject(DATEX_APP_API_URL) private apiUrl: string) {
    this.endpointUrl = `${this.apiUrl}licenseSeats`;
  }

  getAll(): Observable<LicenseSeatVo[]> {
    const url = `${this.endpointUrl}`;
    return this.http.get<LicenseSeatVo[]>(url);
  }

  allocate(id: number, allocateLicenseSeat: AllocateLicenseSeatDto): Observable<any> {
    const url = `${this.endpointUrl}/${id}/allocate`;
    return this.http.post<any>(url, allocateLicenseSeat);
  }
}

export class ApplicationDefinitionTypeNom {

  static values: ApplicationDefinitionType[] = [
    {
      id: 1,
      name: 'Web Application'
    },
    {
      id: 2,
      name: 'Mobile Application'
    },
    {
      id: 3,
      name: 'Component package'
    },
    {
      id: 6,
      name: 'API Application'
    },
    {
      id: 7,
      name: 'Portal Application'
    }
  ];

  public static get(): ApplicationDefinitionType[] {
    return this.values;
  }

  public static getName(applicationDefinitionTypeId: number): string {
    return this.values.find(item => item.id === applicationDefinitionTypeId)?.name;
  }

  public static web(): ApplicationDefinitionType {
    return this.values[0];
  }

  public static mobile(): ApplicationDefinitionType {
    return this.values[1];
  }

  public static componentModule(): ApplicationDefinitionType {
    return this.values[2];
  }

  public static api(): ApplicationDefinitionType {
    return this.values[3];
  }

  public static portal(): ApplicationDefinitionType {
    return this.values[4];
  }

  public static applicationGroup(): number {
    return 1;
  }

  public static componentModuleGroup(): number {
    return 2;
  }
}

export class RoleTypeNom {

  static values: RoleType[] = [
    {
      id: 1,
      name: 'Role'
    },
    {
      id: 2,
      name: 'Data access group'
    }
  ];

  public static getName(roleTypeId: number): string {
    return this.values.find(item => item.id === roleTypeId)?.name;
  }

  public static role(): RoleType {
    return this.values[0];
  }

  public static dataAccessGroup(): RoleType {
    return this.values[1];
  }
}

export class MarketPlaceAppTypeNom {
  static values: MarketPlaceAppType[] = [
    {
      id: 2,
      name: 'Footprint Api'
    },
    {
      id: 3,
      name: 'Wavelength Core'
    },
    {
      id: 4,
      name: 'Wavelength Web Application'
    },
    {
      id: 5,
      name: 'Wavelength Mobile Application'
    },
    {
      id: 6,
      name: 'Wavelength Component Package'
    },
    {
      id: 7,
      name: 'Wavelength API Application'
    },
    {
      id: 8,
      name: 'Wavelength Portal Application'
    }
  ];

  public static get(): MarketPlaceAppType[] {
    return this.values;
  }

  public static getName(marketplaceApplicationTypeId: number): string {
    return this.values.find(item => item.id === marketplaceApplicationTypeId)?.name;
  }

  public static getShortName(marketplaceApplicationTypeId: number): string {
    const name = this.getName(marketplaceApplicationTypeId);
    return name ? name.replace('Wavelength ', '') : 'Unknown';
  }

  public static getIcon(marketplaceApplicationTypeId: number): string {
    if (this.isWavelengthWeb(marketplaceApplicationTypeId)) {
      return 'icon-ic_fluent_globe_20_regular';
    }
    if (this.isWavelengthMobile(marketplaceApplicationTypeId)) {
      return 'icon-ic_fluent_phone_20_regular';
    }
    if (this.isWavelengthPortal(marketplaceApplicationTypeId)) {
      return 'icon-ic_fluent_window_20_regular';
    }
    return 'icon-ic_fluent_certificate_20_regular';
  }

  public static footprintApi(): MarketPlaceAppType {
    return this.values[0];
  }

  public static taskSchedulerApi(): MarketPlaceAppType {
    return this.values[1];
  }

  public static wavelengthWeb(): MarketPlaceAppType {
    return this.values[2];
  }

  public static wavelengthMobile(): MarketPlaceAppType {
    return this.values[3];
  }

  public static wavelengthComponentModule(): MarketPlaceAppType {
    return this.values[4];
  }

  public static wavelengthApi(): MarketPlaceAppType {
    return this.values[5];
  }

  public static wavelengthPortal(): MarketPlaceAppType {
    return this.values[6];
  }

  public static isWavelength(marketPlaceAppTypeId: number): boolean {
    return MarketPlaceAppTypeNom.wavelengthWeb().id === marketPlaceAppTypeId ||
      MarketPlaceAppTypeNom.wavelengthMobile().id === marketPlaceAppTypeId ||
      MarketPlaceAppTypeNom.wavelengthComponentModule().id === marketPlaceAppTypeId ||
      MarketPlaceAppTypeNom.wavelengthApi().id === marketPlaceAppTypeId ||
      MarketPlaceAppTypeNom.wavelengthPortal().id === marketPlaceAppTypeId;
  }

  public static isWavelengthWeb(marketPlaceAppTypeId: number): boolean {
    return MarketPlaceAppTypeNom.wavelengthWeb().id === marketPlaceAppTypeId;
  }

  public static isWavelengthApi(marketPlaceAppTypeId: number): boolean {
    return MarketPlaceAppTypeNom.wavelengthApi().id === marketPlaceAppTypeId;
  }

  public static isWavelengthPortal(marketPlaceAppTypeId: number): boolean {
    return MarketPlaceAppTypeNom.wavelengthPortal().id === marketPlaceAppTypeId;
  }

  public static isWavelengthMobile(marketPlaceAppTypeId: number): boolean {
    return MarketPlaceAppTypeNom.wavelengthMobile().id === marketPlaceAppTypeId;
  }

  public static isFootprintApi(marketPlaceAppTypeId: number): boolean {
    return MarketPlaceAppTypeNom.footprintApi().id === marketPlaceAppTypeId;
  }

  public static isTaskSchedulerApi(marketPlaceAppTypeId: number): boolean {
    return MarketPlaceAppTypeNom.taskSchedulerApi().id === marketPlaceAppTypeId;
  }

  public static getGroupFromMarketPlaceAppType(marketPlaceAppTypeId: number): number {
    if (MarketPlaceAppTypeNom.isWavelength(marketPlaceAppTypeId)) {
      return MarketPlaceAppTypeNom.wavelengthGroup();
    } else if (MarketPlaceAppTypeNom.isFootprintApi(marketPlaceAppTypeId)) {
      return MarketPlaceAppTypeNom.footprintApiGroup();
    } else if (MarketPlaceAppTypeNom.isTaskSchedulerApi(marketPlaceAppTypeId)) {
      return MarketPlaceAppTypeNom.taskSchedulerApiGroup();
    }
    return 0; // unrecognized
  }

  public static wavelengthGroup(): number {
    return 1;
  }

  public static footprintApiGroup(): number {
    return 2;
  }

  public static taskSchedulerApiGroup(): number {
    return 3;
  }
}


